import {TenantInfo} from '../models/tenant.model';
import dayjs from "dayjs";
import { UserPermissions } from '../models/user-permissions.model';
import isBetween from 'dayjs/plugin/isBetween';
import {isTrialExpired} from './trial-utils';

dayjs.extend(isBetween);
export function isLifetimeCustomer(tenant:TenantInfo) {
  return tenant?.instructorPricingPlan?.includes("lifetime");
}

export function isTrialUser(tenant:TenantInfo) {
  return !isTrialExpired(tenant);
}

export const promotionalPeriod = {
  start: 11,
  end: 16
}

/**
 *
 * if the customer is a lifetime user, show them periodically an
 * upgrade offer that gives an incentive to upgrade to a subscription plan.
 *
 */

export function showAppSumoMonthlyUpgradeOffer(tenant: TenantInfo, permissions: UserPermissions) {
  return permissions.isAdmin &&
    isLifetimeCustomer(tenant) &&
    isAppSumoUpgradePeriodOngoing();
}

export function showBlackFridayUpgradeOffer(tenant: TenantInfo, permissions: UserPermissions) {
  return permissions.isAdmin &&
    ( isLifetimeCustomer(tenant) || isTrialUser(tenant) ) &&
    isBlackFridayOngoing();
}

export function showNewYearUpgradeOffer(tenant: TenantInfo, permissions: UserPermissions) {
  return permissions.isAdmin &&
    ( isLifetimeCustomer(tenant) || isTrialUser(tenant) ) &&
    isNewYearPeriodOngoing();
}

export function isAppSumoUpgradePeriodOngoing() {
  const today = dayjs();
  const start = dayjs().date(promotionalPeriod.start ); // 11th of the month
  const end = dayjs().date(promotionalPeriod.end); // 16th of the month

  // skip the monthly banner during December and January, because we don't want to overlap with Black Friday and New Year's offers
  if (isDecemberOrJanuary()) {
    return false;
  }

  // return today.isSame(start, 'day') ||
  //   today.isAfter(start) && today.isSame(end, 'day') ||
  //   today.isBefore(end);

  return today.isBetween(start, end, 'day', '[]');


}

function isDecemberOrJanuary() {
  const today = dayjs();
  const month = today.month(); // Month is zero-indexed (0 = January, 11 = December)

  // Check if the current month is December (11) or January (0)
  return month === 11 || month === 0;
}

function isNewYearPeriodOngoing() {
  // Get today's date
  const today = dayjs();

  // Define start and end of the New Year period
  const startOfNewYear = dayjs(`${today.year()}-01-01`);
  // const endOfNewYear = dayjs(`${today.year()}-01-11`);
  const endOfNewYear = dayjs(`${today.year()}-01-19`); // extended to Jan 19th

  // Check if today is within the New Year period (inclusive)
  return today.isAfter(startOfNewYear.subtract(1, 'day')) && today.isBefore(endOfNewYear.add(1, 'day'));
}

function isBlackFridayOngoing() {

  // debugger;

  const today = dayjs();

  // Get the year
  const year = today.year();

  // Calculate Black Friday date (fourth Friday of November)
  const blackFriday = getBlackFriday();

  // Calculate Cyber Monday (three days after Black Friday)
  const cyberMonday = blackFriday.add(3, 'day');

  // Calculate the start of the Black Friday period (11 days before Black Friday)
  const startOfBlackFridayPeriod = blackFriday.subtract(11, 'day');

  // Calculate the end of the Black Friday period (one day after Cyber Monday)
  const endOfBlackFridayPeriod = cyberMonday.add(1, 'day');

  // Check if today is within the Black Friday period (inclusive)
  return today.isAfter(startOfBlackFridayPeriod.subtract(1, 'day')) && today.isBefore(endOfBlackFridayPeriod.add(1, 'day'));
}

export function getBlackFriday() {
  // Get the first day of November for the current year
  const firstOfNovember = dayjs().month(10).date(1); // November (month is 0-indexed)

  // Find the first Friday of November
  let firstFriday = firstOfNovember;
  while (firstFriday.day() !== 5) { // 5 represents Friday
    firstFriday = firstFriday.add(1, 'day');
  }

  // Calculate the 4th Friday by adding 3 weeks to the first Friday
  const fourthFriday = firstFriday.add(3, 'week');

  return fourthFriday;
}

/**
 *
 * These are the AppSumo users with the original fully unlimited deal.
 *
 */

export function isAppSumoOriginalPlan(tenant:TenantInfo) {
  return tenant?.instructorPricingPlan?.includes("lifetime") && !tenant?.appsumoTierLimits;
}

/**
 *
 * These are the AppSumo or StackCommerce users with the new deal that has usage limits.
 *
 */

export function isLimitedLifetimePlan(tenant:TenantInfo) {
  return tenant?.instructorPricingPlan?.includes("lifetime") && !!tenant?.appsumoTierLimits;
}

export function isStackCommerce(tenant: TenantInfo) {
  return tenant?.lifetimeCampaignName?.toLowerCase()?.includes("stack")
}

export function isStackCommerceWithAppSumoTierLimits(tenant: TenantInfo) {
  return isStackCommerce(tenant) && !!tenant?.appsumoTierLimits;
}

export function isLifetimePlanV2(tenant: TenantInfo) {
  return tenant?.instructorPricingPlan.startsWith("lifetime-pro-v2") && !!tenant?.appsumoTierLimits;
}

export function isLifetimePlanV3(tenant: TenantInfo) {
  return tenant?.instructorPricingPlan.startsWith("lifetime-pro-v3") && !!tenant?.appsumoTierLimits;
}

export function getDealSiteName(tenant: TenantInfo) {
  if (isStackCommerce(tenant)) {
    return "StackCommerce"
  }
  else {
    return "AppSumo";
  }
}
